import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { alertsApi } from '../services/api';
import { useTenantContextStore } from '../store/tenant-context.store';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Bell,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Truck,
  X,
  Check,
  Gauge,
  Battery,
  Zap,
  Shield,
  Volume2,
  Settings,
  List,
  Trash2,
  Play,
  Square,
  TrendingUp,
  TrendingDown,
  Fuel,
  CornerDownRight,
} from 'lucide-react';
import clsx from 'clsx';

type AlertStatus = 'ACTIVE' | 'ACKNOWLEDGED' | 'RESOLVED';
type AlertSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
type AlertType =
  | 'GEOFENCE_ENTER'
  | 'GEOFENCE_EXIT'
  | 'OVERSPEED'
  | 'UNDERSPEED'
  | 'IGNITION_ON'
  | 'IGNITION_OFF'
  | 'DEVICE_OFFLINE'
  | 'BATTERY_LOW'
  | 'FUEL_LOW'
  | 'IDLE_PROLONGED'
  | 'TOWING'
  | 'CRASH'
  | 'JAMMING'
  | 'SOS'
  | 'POWER_CUT'
  | 'POWER_RESTORED'
  | 'LOW_POWER'
  // Movement alerts
  | 'DEVICE_MOVING'
  | 'DEVICE_STOPPED'
  // Driving behavior alerts
  | 'HARD_ACCELERATION'
  | 'HARD_BRAKING'
  | 'HARD_CORNERING'
  // Fuel alerts
  | 'FUEL_DROP'
  | 'FUEL_INCREASE'
  | 'FUEL_THRESHOLD';

interface Alert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  status: AlertStatus;
  message: string;
  details?: Record<string, unknown>;
  latitude?: number;
  longitude?: number;
  triggeredAt: string;
  acknowledgedAt?: string;
  resolvedAt?: string;
  device: {
    id: string;
    name: string;
    plate?: string;
    imei: string;
  };
  rule?: {
    id: string;
    name: string;
  };
}

interface AlertStats {
  totalActive: number;
  totalToday: number;
  byType: Record<string, number>;
  bySeverity: Record<string, number>;
}

const alertTypeLabels: Record<AlertType, string> = {
  GEOFENCE_ENTER: 'Entrée zone',
  GEOFENCE_EXIT: 'Sortie zone',
  OVERSPEED: 'Excès de vitesse',
  UNDERSPEED: 'Vitesse trop basse',
  IGNITION_ON: 'Contact allumé',
  IGNITION_OFF: 'Contact éteint',
  DEVICE_OFFLINE: 'Appareil hors ligne',
  BATTERY_LOW: 'Batterie faible',
  FUEL_LOW: 'Carburant bas',
  IDLE_PROLONGED: 'Ralenti prolongé',
  TOWING: 'Remorquage',
  CRASH: 'Collision',
  JAMMING: 'Brouillage GPS',
  SOS: 'SOS',
  POWER_CUT: 'Courant coupé',
  POWER_RESTORED: 'Courant restauré',
  LOW_POWER: 'Alimentation faible',
  // Movement alerts
  DEVICE_MOVING: 'Véhicule en mouvement',
  DEVICE_STOPPED: 'Véhicule arrêté',
  // Driving behavior alerts
  HARD_ACCELERATION: 'Accélération brusque',
  HARD_BRAKING: 'Freinage brusque',
  HARD_CORNERING: 'Virage brusque',
  // Fuel alerts
  FUEL_DROP: 'Chute de carburant',
  FUEL_INCREASE: 'Plein de carburant',
  FUEL_THRESHOLD: 'Seuil carburant dépassé',
};

const alertTypeIcons: Record<AlertType, React.ElementType> = {
  GEOFENCE_ENTER: MapPin,
  GEOFENCE_EXIT: MapPin,
  OVERSPEED: Gauge,
  UNDERSPEED: Gauge,
  IGNITION_ON: Zap,
  IGNITION_OFF: Zap,
  DEVICE_OFFLINE: AlertCircle,
  BATTERY_LOW: Battery,
  FUEL_LOW: Gauge,
  IDLE_PROLONGED: Clock,
  TOWING: Truck,
  CRASH: AlertTriangle,
  JAMMING: Shield,
  SOS: Volume2,
  POWER_CUT: Zap,
  POWER_RESTORED: Zap,
  LOW_POWER: Battery,
  // Movement alerts
  DEVICE_MOVING: Play,
  DEVICE_STOPPED: Square,
  // Driving behavior alerts
  HARD_ACCELERATION: TrendingUp,
  HARD_BRAKING: TrendingDown,
  HARD_CORNERING: CornerDownRight,
  // Fuel alerts
  FUEL_DROP: Fuel,
  FUEL_INCREASE: Fuel,
  FUEL_THRESHOLD: Fuel,
};

const severityColors: Record<AlertSeverity, string> = {
  LOW: 'bg-blue-100 text-blue-800',
  MEDIUM: 'bg-yellow-100 text-yellow-800',
  HIGH: 'bg-orange-100 text-orange-800',
  CRITICAL: 'bg-red-100 text-red-800',
};

const statusColors: Record<AlertStatus, string> = {
  ACTIVE: 'bg-red-100 text-red-800',
  ACKNOWLEDGED: 'bg-yellow-100 text-yellow-800',
  RESOLVED: 'bg-green-100 text-green-800',
};

const statusLabels: Record<AlertStatus, string> = {
  ACTIVE: 'Active',
  ACKNOWLEDGED: 'Acquittée',
  RESOLVED: 'Résolue',
};

export default function AlertsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';

  // Filters
  const [statusFilter, setStatusFilter] = useState<AlertStatus | ''>('');
  const [severityFilter, setSeverityFilter] = useState<AlertSeverity | ''>('');
  const [typeFilter, setTypeFilter] = useState<AlertType | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAlerts, setSelectedAlerts] = useState<Set<string>>(new Set());
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);

  const itemsPerPage = 20;

  // Fetch alerts
  const { data: alertsData, isLoading: alertsLoading } = useQuery({
    queryKey: ['alerts', statusFilter, severityFilter, typeFilter, currentPage, tenantKey],
    queryFn: async () => {
      const params: Record<string, unknown> = {
        page: currentPage,
        limit: itemsPerPage,
      };
      if (statusFilter) params.status = statusFilter;
      if (severityFilter) params.severity = severityFilter;
      if (typeFilter) params.type = typeFilter;

      const response = await alertsApi.list(params as any);
      return response.data;
    },
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['alert-stats', tenantKey],
    queryFn: async () => {
      const response = await alertsApi.getStats();
      return response.data as AlertStats;
    },
  });

  // Mutations
  const acknowledgeMutation = useMutation({
    mutationFn: (id: string) => alertsApi.acknowledge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => alertsApi.resolve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });

  const bulkAcknowledgeMutation = useMutation({
    mutationFn: (ids: string[]) => alertsApi.bulkAcknowledge(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
      setSelectedAlerts(new Set());
    },
  });

  const bulkResolveMutation = useMutation({
    mutationFn: (ids: string[]) => alertsApi.bulkResolve(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
      setSelectedAlerts(new Set());
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => alertsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => alertsApi.bulkDelete(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
      setSelectedAlerts(new Set());
    },
  });

  const alerts: Alert[] = alertsData?.data || [];
  const pagination = alertsData?.pagination || { total: 0, totalPages: 1 };

  // Filter by search
  const filteredAlerts = alerts.filter((alert) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      alert.message.toLowerCase().includes(query) ||
      alert.device.name.toLowerCase().includes(query) ||
      alert.device.plate?.toLowerCase().includes(query)
    );
  });

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, severityFilter, typeFilter]);

  const handleSelectAll = () => {
    if (selectedAlerts.size === filteredAlerts.length) {
      setSelectedAlerts(new Set());
    } else {
      setSelectedAlerts(new Set(filteredAlerts.map((a) => a.id)));
    }
  };

  const handleSelectAlert = (id: string) => {
    const newSelected = new Set(selectedAlerts);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedAlerts(newSelected);
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('alerts.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('alerts.active_count', { count: statsData?.totalActive || 0 })}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <Link
          to="/alerts"
          className={clsx(
            'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
            location.pathname === '/alerts'
              ? 'border-green-600 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          <List className="h-4 w-4" />
          {t('alerts.tab_history')}
        </Link>
        <Link
          to="/alerts/rules"
          className={clsx(
            'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
            location.pathname === '/alerts/rules'
              ? 'border-green-600 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          <Settings className="h-4 w-4" />
          {t('alerts.tab_rules')}
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('alerts.stat_active')}
          value={statsData?.totalActive || 0}
          icon={AlertTriangle}
          color="bg-red-500"
        />
        <StatCard
          title={t('alerts.stat_today')}
          value={statsData?.totalToday || 0}
          icon={Clock}
          color="bg-blue-500"
        />
        <StatCard
          title={t('alerts.stat_critical')}
          value={statsData?.bySeverity?.CRITICAL || 0}
          icon={AlertCircle}
          color="bg-orange-500"
        />
        <StatCard
          title={t('alerts.stat_overspeed')}
          value={statsData?.byType?.OVERSPEED || 0}
          icon={Gauge}
          color="bg-purple-500"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('alerts.search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-5 w-5 text-gray-400" />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AlertStatus | '')}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{t('alerts.filter_all_statuses')}</option>
            <option value="ACTIVE">{t('alerts.status_active')}</option>
            <option value="ACKNOWLEDGED">{t('alerts.status_acknowledged')}</option>
            <option value="RESOLVED">{t('alerts.status_resolved')}</option>
          </select>

          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as AlertSeverity | '')}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{t('alerts.filter_all_severities')}</option>
            <option value="CRITICAL">{t('alerts.severity_critical')}</option>
            <option value="HIGH">{t('alerts.severity_high')}</option>
            <option value="MEDIUM">{t('alerts.severity_medium')}</option>
            <option value="LOW">{t('alerts.severity_low')}</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as AlertType | '')}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{t('alerts.filter_all_types')}</option>
            {Object.entries(alertTypeLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedAlerts.size > 0 && (
        <div className="flex items-center gap-4 p-3 bg-primary-50 rounded-lg">
          <span className="text-sm font-medium text-primary-700">
            {t('alerts.selected_count', { count: selectedAlerts.size })}
          </span>
          <button
            onClick={() => bulkAcknowledgeMutation.mutate(Array.from(selectedAlerts))}
            disabled={bulkAcknowledgeMutation.isPending}
            className="px-3 py-1 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
          >
            {t('alerts.acknowledge')}
          </button>
          <button
            onClick={() => bulkResolveMutation.mutate(Array.from(selectedAlerts))}
            disabled={bulkResolveMutation.isPending}
            className="px-3 py-1 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            {t('alerts.resolve')}
          </button>
          <button
            onClick={() => {
              if (confirm(t('alerts.delete_confirm_count', { count: selectedAlerts.size }))) {
                bulkDeleteMutation.mutate(Array.from(selectedAlerts));
              }
            }}
            disabled={bulkDeleteMutation.isPending}
            className="px-3 py-1 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            {t('alerts.delete')}
          </button>
          <button
            onClick={() => setSelectedAlerts(new Set())}
            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
          >
            {t('alerts.cancel')}
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 w-12 hidden md:table-cell">
                  <input
                    type="checkbox"
                    checked={
                      filteredAlerts.length > 0 && selectedAlerts.size === filteredAlerts.length
                    }
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('alerts.th_alert')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  {t('alerts.th_vehicle')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  {t('alerts.th_severity')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('alerts.th_status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  {t('alerts.th_date')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('alerts.th_actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {alertsLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    Chargement...
                  </td>
                </tr>
              ) : filteredAlerts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    <Bell className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p>Aucune alerte trouvée</p>
                  </td>
                </tr>
              ) : (
                filteredAlerts.map((alert) => {
                  const TypeIcon = alertTypeIcons[alert.type] || AlertTriangle;
                  return (
                    <tr
                      key={alert.id}
                      className={clsx(
                        'hover:bg-gray-50 cursor-pointer',
                        selectedAlerts.has(alert.id) && 'bg-primary-50'
                      )}
                      onClick={() => setSelectedAlert(alert)}
                    >
                      <td className="px-4 py-4 hidden md:table-cell" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedAlerts.has(alert.id)}
                          onChange={() => handleSelectAlert(alert.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div
                            className={clsx(
                              'p-2 rounded-lg mr-3',
                              alert.severity === 'CRITICAL' && 'bg-red-100',
                              alert.severity === 'HIGH' && 'bg-orange-100',
                              alert.severity === 'MEDIUM' && 'bg-yellow-100',
                              alert.severity === 'LOW' && 'bg-blue-100'
                            )}
                          >
                            <TypeIcon
                              className={clsx(
                                'h-5 w-5',
                                alert.severity === 'CRITICAL' && 'text-red-600',
                                alert.severity === 'HIGH' && 'text-orange-600',
                                alert.severity === 'MEDIUM' && 'text-yellow-600',
                                alert.severity === 'LOW' && 'text-blue-600'
                              )}
                            />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {alertTypeLabels[alert.type]}
                            </div>
                            <div className="text-sm text-gray-500 line-clamp-1">{alert.message}</div>
                            <div className="text-xs text-gray-400 md:hidden mt-0.5">
                              {alert.device.name} · {formatDistanceToNow(new Date(alert.triggeredAt), { addSuffix: true, locale: fr })}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <div className="text-sm font-medium text-gray-900">{alert.device.name}</div>
                        {alert.device.plate && (
                          <div className="text-sm text-gray-500">{alert.device.plate}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <span
                          className={clsx(
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                            severityColors[alert.severity]
                          )}
                        >
                          {alert.severity}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={clsx(
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                            statusColors[alert.status]
                          )}
                        >
                          {statusLabels[alert.status]}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 hidden md:table-cell">
                        <div
                          title={format(new Date(alert.triggeredAt), 'PPpp', { locale: fr })}
                        >
                          {formatDistanceToNow(new Date(alert.triggeredAt), {
                            addSuffix: true,
                            locale: fr,
                          })}
                        </div>
                      </td>
                      <td
                        className="px-4 py-4 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-end gap-1">
                          {alert.status === 'ACTIVE' && (
                            <button
                              onClick={() => acknowledgeMutation.mutate(alert.id)}
                              disabled={acknowledgeMutation.isPending}
                              title={t('alerts.acknowledge')}
                              className="p-1.5 text-yellow-600 hover:bg-yellow-100 rounded-lg transition-colors"
                            >
                              <Clock className="h-4 w-4" />
                            </button>
                          )}
                          {alert.status !== 'RESOLVED' && (
                            <button
                              onClick={() => resolveMutation.mutate(alert.id)}
                              disabled={resolveMutation.isPending}
                              title={t('alerts.resolve')}
                              className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (confirm('Supprimer cette alerte ?')) {
                                deleteMutation.mutate(alert.id);
                              }
                            }}
                            disabled={deleteMutation.isPending}
                            title={t('alerts.delete')}
                            className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Page {currentPage} sur {pagination.totalPages} ({pagination.total} alertes)
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={currentPage === pagination.totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <AlertDetailModal
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
          onAcknowledge={() => {
            acknowledgeMutation.mutate(selectedAlert.id);
            setSelectedAlert(null);
          }}
          onResolve={() => {
            resolveMutation.mutate(selectedAlert.id);
            setSelectedAlert(null);
          }}
          onDelete={() => {
            if (confirm('Supprimer cette alerte ?')) {
              deleteMutation.mutate(selectedAlert.id);
              setSelectedAlert(null);
            }
          }}
        />
      )}
    </div>
  );
}

function StatCard({
  title,
  value,
  icon: Icon,
  color,
}: {
  title: string;
  value: number;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex items-center">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div className="ml-4">
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}

function AlertDetailModal({
  alert,
  onClose,
  onAcknowledge,
  onResolve,
  onDelete,
}: {
  alert: Alert;
  onClose: () => void;
  onAcknowledge: () => void;
  onResolve: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const TypeIcon = alertTypeIcons[alert.type] || AlertTriangle;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div className="flex items-center gap-3">
              <div
                className={clsx(
                  'p-2 rounded-lg',
                  alert.severity === 'CRITICAL' && 'bg-red-100',
                  alert.severity === 'HIGH' && 'bg-orange-100',
                  alert.severity === 'MEDIUM' && 'bg-yellow-100',
                  alert.severity === 'LOW' && 'bg-blue-100'
                )}
              >
                <TypeIcon
                  className={clsx(
                    'h-6 w-6',
                    alert.severity === 'CRITICAL' && 'text-red-600',
                    alert.severity === 'HIGH' && 'text-orange-600',
                    alert.severity === 'MEDIUM' && 'text-yellow-600',
                    alert.severity === 'LOW' && 'text-blue-600'
                  )}
                />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {alertTypeLabels[alert.type]}
                </h2>
                <span
                  className={clsx(
                    'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                    statusColors[alert.status]
                  )}
                >
                  {statusLabels[alert.status]}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Message */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-900">{alert.message}</p>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500">Véhicule</p>
                <p className="font-medium text-gray-900">{alert.device.name}</p>
                {alert.device.plate && (
                  <p className="text-sm text-gray-500">{alert.device.plate}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500">Sévérité</p>
                <span
                  className={clsx(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    severityColors[alert.severity]
                  )}
                >
                  {alert.severity}
                </span>
              </div>
              <div>
                <p className="text-xs text-gray-500">Déclenchée</p>
                <p className="font-medium text-gray-900">
                  {format(new Date(alert.triggeredAt), 'PPpp', { locale: fr })}
                </p>
              </div>
              {alert.rule && (
                <div>
                  <p className="text-xs text-gray-500">Règle</p>
                  <p className="font-medium text-gray-900">{alert.rule.name}</p>
                </div>
              )}
            </div>

            {/* Location */}
            {alert.latitude && alert.longitude && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span className="font-mono">
                    {alert.latitude.toFixed(6)}, {alert.longitude.toFixed(6)}
                  </span>
                </div>
              </div>
            )}

            {/* Timeline */}
            {(alert.acknowledgedAt || alert.resolvedAt) && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-medium">Historique</p>
                <div className="space-y-2">
                  <TimelineItem
                    icon={AlertTriangle}
                    label="Déclenchée"
                    date={alert.triggeredAt}
                    color="text-red-600"
                  />
                  {alert.acknowledgedAt && (
                    <TimelineItem
                      icon={Clock}
                      label="Acquittée"
                      date={alert.acknowledgedAt}
                      color="text-yellow-600"
                    />
                  )}
                  {alert.resolvedAt && (
                    <TimelineItem
                      icon={Check}
                      label="Résolue"
                      date={alert.resolvedAt}
                      color="text-green-600"
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
            <button
              onClick={onDelete}
              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
              title={t('alerts.delete')}
            >
              <Trash2 className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            >
              Fermer
            </button>
            {alert.status === 'ACTIVE' && (
              <button
                onClick={onAcknowledge}
                className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
              >
                Acquitter
              </button>
            )}
            {alert.status !== 'RESOLVED' && (
              <button
                onClick={onResolve}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Résoudre
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelineItem({
  icon: Icon,
  label,
  date,
  color,
}: {
  icon: React.ElementType;
  label: string;
  date: string;
  color: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <Icon className={`h-4 w-4 ${color}`} />
      <span className="text-sm text-gray-600">{label}</span>
      <span className="text-sm text-gray-400">
        {format(new Date(date), 'PPpp', { locale: fr })}
      </span>
    </div>
  );
}
